import React from 'react';
import {ActionableNotification, FeatureFlags, InlineNotification} from '@carbon/react';
import RosterName from './roster-name';
import type {ComparisonSummary, SummaryBlocker} from './summarise';

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

/**
 * How the notices name the geography: its name, or "this county" when none is set
 *
 * @param geography - County, ZIP or state the comparison is scoped to
 * @param noun - What the geography grain calls one place: 'county' or 'ZIP code'
 */
export const placeName = (geography: string, noun: string) => geography || `this ${noun}`;

type NoticeProps = {
    kind: 'error' | 'info';
    title: string;
    body: string;
    'data-testid': string;
};

// Carbon's low-contrast inline notification, filling the panel. For a notice with nothing to
// click: Carbon forbids interactive children in this one
export const SummaryNotice = ({kind, title, body, 'data-testid': testId}: NoticeProps) => (
    <InlineNotification
        kind={kind}
        lowContrast
        hideCloseButton
        title={title}
        subtitle={body}
        className='summary-panel__notice'
        data-testid={testId}
    />
);

type BlockedProps = {
    blocker: SummaryBlocker;
    summary: ComparisonSummary;
    place: string;
    noun: string;
};

type CopyContext = {place: string; noun: string; primaryName: string};

const BLOCKER_COPY: Record<SummaryBlocker, (context: CopyContext) => {title: string; body: string}> = {
    'no-data': ({place, noun}) => ({
        title: `No data for ${place}`,
        body: `None of the selected rosters have data for ${place}. Choose a different ${noun} or add a roster that covers it.`,
    }),
    'primary-no-data': ({place, primaryName}) => ({
        title: 'Primary roster has no data',
        body: `Your primary roster, ${primaryName}, has no data for ${place}. Select a different primary.`,
    }),
    // A comparison needs two rosters, so this is always one
    'too-few': ({place, noun}) => ({
        title: 'Not enough data to compare',
        body: `Only 1 selected roster has data for ${place}. Add another roster or choose a different ${noun}.`,
    }),
    'no-common': () => ({
        title: 'No specialties in common',
        body: 'The selected rosters have no specialty with data in all of them.',
    }),
};

/**
 * A data gap the summary can't work around, in place of the summary. Most specific first, as
 * summariseComparison ranks them. Red only here; a gap it can work around is GapWarning's
 */
export const BlockedSummary = ({blocker, summary, place, noun}: BlockedProps) => {
    const primaryName = summary.excluded.find((r) => r.isPrimary)?.name ?? '';
    const {title, body} = BLOCKER_COPY[blocker]({place, noun, primaryName});
    return <SummaryNotice kind='error' title={title} body={body} data-testid='summary-panel-blocked' />;
};

type GapWarningProps = {
    summary: ComparisonSummary;
    place: string;
    showLeftOut: boolean;
    onToggleLeftOut: () => void;
};

/**
 * The data gaps the summary works around: rosters excluded for having no data, and specialties
 * left out because a roster lacks them. One warning for both, never two stacked, with the
 * left-out specialties behind "Show which"
 *
 * ActionableNotification, not InlineNotification, as "Show which" is interactive; see
 * RosterListError for the role and flag. Escape is turned off, as it would close the warning
 * for the rest of the visit while focus is on its button
 */
export const GapWarning = ({summary, place, showLeftOut, onToggleLeftOut}: GapWarningProps) => {
    const {excluded, partial, leftOut} = summary;
    const title = [
        excluded.length ? `${excluded.length} ${plural(excluded.length, 'roster', 'rosters')} excluded` : '',
        leftOut.length ? `${leftOut.length} ${plural(leftOut.length, 'specialty', 'specialties')} left out` : '',
    ].filter(Boolean).join(', ');

    return (
        <FeatureFlags enableExperimentalFocusWrapWithoutSentinels>
            <ActionableNotification
                inline
                kind='warning'
                lowContrast
                hideCloseButton
                closeOnEscape={false}
                role='status'
                title={title}
                subtitle={(
                    <>
                        {/* Flex items render no whitespace, but assistive tech needs it after the name */}
                        {excluded.map((roster) => (
                            <p key={roster.key}>
                                <RosterName roster={roster} />{' '}
                                has no data for {place}, so it isn’t included in this summary.
                            </p>
                        ))}
                        {partial.map((roster) => (
                            <p key={roster.key}>
                                <RosterName roster={roster} />{' '}
                                has no data for {roster.missing} {plural(roster.missing, 'specialty', 'specialties')} in{' '}
                                {place}, so {plural(roster.missing, 'it’s', 'they’re')} left out of every roster’s figures.
                            </p>
                        ))}
                    </>
                )}
                className='summary-panel__notice'
                data-testid='summary-gaps'
            >
                {leftOut.length > 0 && (
                    <>
                        <button
                            type='button'
                            className='summary-panel__gaps-toggle'
                            aria-expanded={showLeftOut}
                            aria-controls='summary-left-out'
                            onClick={onToggleLeftOut}
                        >
                            {showLeftOut ? 'Hide' : 'Show which'}
                        </button>
                        <ul id='summary-left-out' className='summary-panel__left-out' hidden={!showLeftOut}>
                            {leftOut.map((name, i) => <li key={`${name}-${i}`}>{name}</li>)}
                        </ul>
                    </>
                )}
            </ActionableNotification>
        </FeatureFlags>
    );
};
